// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import AddButton from 'Ui/components/settings/common/add-button';
import EditLink from 'Ui/components/common/data-table/edit-link';
import {closeDialog, openDialog} from 'Store/actions/dialogs';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import {deleteJobCategoryById, getJobTemplates} from 'Services/job-service';
import {fetchEffectData, useDeepEffect} from 'Utils/react-utils';
import {replaceTranslationNames} from 'Utils/translation-utils';
import dialogTypes from 'Ui/components/common/dialog-types';
import {TOAST_TYPE} from 'Src/ui-core/constants/toast-constants';
import DataGridWrapper from 'Src/ui-core/components/common/data-table/data-grid-wrapper';

function getDataGridColumns(
    translations,
    jobCategories,
    openJobCategoryDialog,
    fetchJobTemplates
) {
    return [
        {
            headerName: translations.ONLINK_JOB_CATEGORY,
            field: 'title',
            flex: 1,
            renderCell: (params) => {
                return (
                    <EditLink
                        onClick={() =>
                            openJobCategoryDialog({
                                jobCategory: params.row,
                                membershipJobCategories: jobCategories,
                                updateJobCategories: async () => {
                                    await fetchJobTemplates();
                                }
                            })
                        }
                    >
                        {params.value}
                    </EditLink>
                );
            }
        }
    ];
}

function getFilterComponent(
    translations,
    jobCategories,
    openJobCategoryDialog,
    fetchJobTemplates
) {
    return (
        <div className='extra-table-options'>
            <AddButton
                className='add-job-category-button'
                label={replaceTranslationNames(translations.ONLINK_ADD_ITEM, {
                    '0': translations.ONLINK_JOB_CATEGORY
                })}
                onClick={() =>
                    openJobCategoryDialog({
                        jobCategory: null,
                        membershipJobCategories: jobCategories,
                        updateJobCategories: async () => {
                            await fetchJobTemplates();
                        }
                    })
                }
            />
        </div>
    );
}

function JobCategoriesSettings(props) {
    const {
        closeConfirmation,
        openConfirmation,
        openJobCategoryDialog,
        translations
    } = props;

    const {addToast} = useToast();
    const [loading, setLoading] = React.useState(false);
    const [jobCategories, setJobCategories] = React.useState([]);
    const [jobTemplates, setJobTemplates] = React.useState([]);

    async function fetchJobTemplates(isMounted = () => true) {
        setLoading(true);

        const {
            templates, jobCategories
        } = await getJobTemplates();

        if (isMounted()) {
            setJobCategories(jobCategories);
            setJobTemplates(templates);
            setLoading(false);
        }
    }

    function deleteJobCategory(jobCategory) {
        openConfirmation({
            message: replaceTranslationNames(
                translations.ONLINK_ITEM_THIS_CANNOT_BE_UNDONE,
                {
                    '0': translations.ONLINK_JOB_CATEGORY.toLowerCase()
                }
            ),
            title: replaceTranslationNames(translations.ONLINK_DELETE_ITEM, {
                '0': translations.ONLINK_JOB_CATEGORY
            }),
            async onContinue() {
                closeConfirmation();

                try {
                    await deleteJobCategoryById(jobCategory.jobCategoryId);

                    await fetchJobTemplates();
                } catch {
                    addToast({
                        message: replaceTranslationNames(
                            translations.ONLINK_CONFIRM_ITEM_DELETION_FAILED,
                            {
                                '0': translations.ONLINK_JOB_CATEGORY.toLowerCase()
                            }
                        ),
                        type: TOAST_TYPE.ERROR
                    });
                }
            },
            onCancel: closeConfirmation
        });
    }

    useDeepEffect(() => fetchEffectData(fetchJobTemplates), []);

    return (
        <div className={'settings-container data-grid'}>
            <DataGridWrapper
                columns={getDataGridColumns(
                    translations,
                    jobCategories,
                    openJobCategoryDialog,
                    fetchJobTemplates
                )}
                deleteFilter={(jobCategory) =>
                    !jobTemplates.some(
                        (template) =>
                            template.jobCategory === jobCategory.title
                    )
                }
                deleteHandler={deleteJobCategory}
                filterComponent={getFilterComponent(
                    translations,
                    jobCategories,
                    openJobCategoryDialog,
                    fetchJobTemplates
                )}
                getRowId={(row) => row.jobCategoryId}
                initialState={{
                    sorting: {
                        sortModel: [
                            {
                                field: 'title',
                                sort: 'asc'
                            }
                        ]
                    }
                }}
                loading={loading}
                rows={jobCategories}
                searchFields={['title']}
                searchable={true}
                showDeleteColumn={true}
                slotProps={{
                    root: {
                        disableColumnMenu: true
                    }
                }}
                translations={translations}
            />
        </div>
    );
}

JobCategoriesSettings.propTypes = {
    closeConfirmation: PropTypes.func,
    openConfirmation: PropTypes.func,
    openJobCategoryDialog: PropTypes.func,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        translations: state.translations
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        closeConfirmation() {
            dispatch(closeDialog(dialogTypes.CONFIRMATION_DIALOG));
        },
        openConfirmation(props) {
            dispatch(openDialog(dialogTypes.CONFIRMATION_DIALOG, props));
        },
        openJobCategoryDialog(props) {
            dispatch(openDialog(dialogTypes.JOB_CATEGORY_DIALOG, props));
        }
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(JobCategoriesSettings);
